import React, { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import Loading from './components/loading';
import { router } from './route';
import { checkAuthState } from './services/utils/auth';
import { UserContext } from './services/contexts/user-context';
import { useGetMe } from './services/api/users-api';
import { trackPageView } from './services/firebase/firebase-config';

const initialLocation = router.state.location;
let previousLocationKey = `${initialLocation.pathname}${initialLocation.search}${initialLocation.hash}`;

trackPageView(initialLocation);

router.subscribe((routerState) => {
  const { location } = routerState;
  const locationKey = `${location.pathname}${location.search}${location.hash}`;

  if (locationKey === previousLocationKey) {
    return;
  }

  previousLocationKey = locationKey;
  trackPageView(location);
});

function MainApp() {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check Firebase auth state
  useEffect(() => {
    const unsubscribe = checkAuthState(setIsAuthenticated, setLoading);
    return () => unsubscribe();
  }, []);

  // Fetch user info from backend
  const { data: meData, isLoading: meLoading, isError: meError } = useGetMe({
    enabled: isAuthenticated,
  });

  // Redirect logic — only handles login/public routing
  useEffect(() => {
    if (!loading) {
      const currentPath = router.state.location.pathname;
      const profileWithName = /^\/profile\/[^/]+$/;

      const isPublicRoute =
        currentPath === '/' ||
        currentPath === '/login' ||
        profileWithName.test(currentPath);

      // Only redirect away from login if backend user data is confirmed
      const backendUser = meData?.data;
      if (isAuthenticated && backendUser && currentPath === '/login') {
        const homePath = backendUser.role === 'recruiter' ? '/recruiter/home' : '/default/profile';
        router.navigate(homePath);
      } else if (!isAuthenticated && !isPublicRoute) {
        router.navigate('/login');
      }
    }
  }, [isAuthenticated, loading, meData]);

  // Only block rendering on the initial Firebase auth check
  // Once auth is resolved, render immediately — RoleGuard handles the rest
  if (loading) return <div><Loading /></div>;

  // Determine if user data is still loading (only relevant if authenticated)
  const userLoading = isAuthenticated && meLoading && !meError;

  return (
    <UserContext.Provider value={{ user: meData?.data || null, isLoading: userLoading, error: meError }}>
      <RouterProvider router={router} />
    </UserContext.Provider>
  );
}

export default MainApp;
